/**
 * Scan targets for jobs sent to an edge agent (Db2 for i Phase 2).
 *
 * Pure and unit-tested; EdgeAgentScanForm is the UI shell over it. The target
 * NAMES a credential held on the agent host and never carries a password: the
 * controller refuses inline secrets, and the password must not pass through
 * FOCTTA at all. Shapes match apps/edge-discovery/src/connector.rs and
 * interop/contracts/edge-targets/db2_for_i.fields.json.
 */
import type { EdgePodSummary } from './api';

export interface Db2ForIForm {
  system: string;
  username: string;
  credential: string;
  scope: 'library' | 'whole_system' | 'ifs';
  library: string;
  ifsPath: string;
  libraryDenylist: string;
  useTls: boolean;
  iasp: string;
  hexFieldCcsid: string;
  driverName: string;
  includeSourceFiles: boolean;
  includeLogicalFiles: boolean;
}

export const DB2I_DEFAULT_FORM: Db2ForIForm = {
  system: '', username: '', credential: '', scope: 'whole_system', library: '', ifsPath: '', libraryDenylist: '',
  useTls: true, iasp: '', hexFieldCcsid: '37', driverName: '', includeSourceFiles: false, includeLogicalFiles: false,
};

export const CREDENTIAL_NAME_RE = /^[a-z0-9][a-z0-9_-]{0,63}$/;

/**
 * The agent's own default (`default_db2i_library_denylist` in connector.rs).
 * Repeated here because a target that carries `library_denylist` REPLACES the
 * agent default rather than adding to it, so the console always sends these
 * with the operator's additions.
 */
export const DB2I_DEFAULT_LIBRARY_DENYLIST: readonly string[] = [
  'QSYS', 'QSYS2', 'QTEMP', 'QGPL', 'QUSRSYS', 'QRECOVERY', 'QSPL', 'QRPLOBJ',
];

/** The agent refuses these too (`ifs::validate_ifs_path`); saying so here saves a queued scan that fails on the agent. */
const IFS_DENIED_ROOTS = ['/QSYS.LIB', '/QNTC', '/QFILESVR.400', '/QOPT'];

/** The agent's `MAX_IFS_ROOT_CHARS`, counted the same way (code points, not UTF-16 units). */
export const MAX_IFS_ROOT_CHARS = 512;

/**
 * Refused anywhere in the path, BEFORE trimming, exactly as the agent's
 * `refused_path_char`: control characters (U+0085 among them) and U+FEFF.
 * JavaScript's trim strips U+FEFF and Rust's strips U+0085, so trimming
 * either first made the console and the agent disagree.
 */
function hasRefusedCharacter(s: string): boolean {
  for (let i = 0; i < s.length; i += 1) {
    const c = s.charCodeAt(i);
    if (c < 0x20 || (c >= 0x7f && c <= 0x9f) || c === 0xfeff) return true;
  }
  return false;
}

/**
 * The same normalisation as the agent's `normalise_ifs_path`, so the denied
 * roots are compared against the path the IBM i will actually resolve: trim,
 * collapse repeated slashes, drop `.`, let `..` remove the segment before it,
 * and refuse a `..` that climbs above `/` or a name that starts or ends with a
 * space (`/ QNTC`). Returns the normalised path, or why it was refused.
 */
export function normaliseIfsPath(raw: string): { ok: true; path: string } | { ok: false; error: string } {
  if (hasRefusedCharacter(raw)) {
    return { ok: false, error: 'That path holds a control character or a byte-order mark. Type the directory again, such as /data/extracts.' };
  }
  const path = raw.trim();
  if (!path.startsWith('/')) {
    return { ok: false, error: 'Enter an absolute IFS directory such as /data/extracts.' };
  }
  const segments: string[] = [];
  for (const seg of path.split('/')) {
    if (seg === '' || seg === '.') continue;
    if (seg === '..') {
      if (segments.length === 0) return { ok: false, error: 'That path climbs above / with "..". Enter the directory itself, such as /data/extracts.' };
      segments.pop();
      continue;
    }
    if (seg.trim() !== seg) return { ok: false, error: 'A directory name in that path starts or ends with a space. Enter the path without the extra spaces.' };
    segments.push(seg);
  }
  return { ok: true, path: `/${segments.join('/')}` };
}

function ifsDenied(normal: string): boolean {
  const segs = normal.toUpperCase().split('/').filter((s) => s !== '');
  const first = segs[0] ?? '';
  const second = segs[1] ?? '';
  return IFS_DENIED_ROOTS.some((r) => r.slice(1) === first) || second === 'QSYS.LIB';
}

/** The normalised path to send, or why it is refused. Mirrors the agent's `validate_ifs_path`. */
export function validateIfsPath(raw: string): { ok: true; path: string } | { ok: false; error: string } {
  if (Array.from(raw).length > MAX_IFS_ROOT_CHARS) {
    return { ok: false, error: `That path is longer than ${MAX_IFS_ROOT_CHARS} characters. Enter a shorter IFS directory.` };
  }
  const n = normaliseIfsPath(raw);
  if (!n.ok) return n;
  if (n.path === '/') return { ok: false, error: 'Enter the IFS directory to scan, for example /home or /data. The whole file system (/) is not scanned.' };
  if (ifsDenied(n.path)) {
    return { ok: false, error: 'That path is not stream-file storage on this partition. Library members are scanned by choosing a library; /QNTC and /QFILESVR.400 reach other machines.' };
  }
  return n;
}

export function sameCredentialName(a: string, b: string): boolean {
  // The agent reports environment-provided names with underscores, because a
  // dash cannot appear in an environment variable name.
  return a.replace(/-/g, '_') === b.replace(/-/g, '_');
}

function credentialEnvVar(name: string): string {
  return `FOCTTA_CREDENTIAL_${name.toUpperCase().replace(/-/g, '_')}`;
}

export function buildDb2ForITarget(
  form: Db2ForIForm,
): { ok: true; target: Record<string, unknown> } | { ok: false; error: string } {
  const system = form.system.trim();
  const username = form.username.trim();
  const credential = form.credential.trim();
  if (!system) return { ok: false, error: 'Enter the IBM i system (host name or address).' };
  if (!username) return { ok: false, error: 'Enter the user profile the scan connects as.' };
  if (!CREDENTIAL_NAME_RE.test(credential)) {
    return { ok: false, error: 'The credential name must be lowercase letters, digits, - or _, up to 64 characters.' };
  }
  const ccsid = Number(form.hexFieldCcsid.trim() || '37');
  if (!Number.isInteger(ccsid) || ccsid < 1 || ccsid > 65534) {
    return { ok: false, error: 'The CCSID for *HEX fields must be a whole number between 1 and 65534.' };
  }
  const target: Record<string, unknown> = {
    kind: 'db2_for_i',
    system,
    username,
    password_credential: credential,
  };
  if (form.scope === 'library') {
    const library = form.library.trim();
    if (!library) return { ok: false, error: 'Enter the library to scan, or choose the whole system.' };
    // Uppercase to match the catalogue's system name exactly, same reason the deny list is uppercased.
    target['library'] = library.toUpperCase();
  } else if (form.scope === 'ifs') {
    const ifs = validateIfsPath(form.ifsPath);
    if (!ifs.ok) return { ok: false, error: ifs.error };
    // The normalised path, so what was checked is what the agent walks.
    target['ifs_path'] = ifs.path;
  } else {
    const added = form.libraryDenylist
      .split(',')
      .map((l) => l.trim().toUpperCase())
      .filter((l) => l.length > 0);
    if (added.length > 0) {
      target['library_denylist'] = [...new Set([...DB2I_DEFAULT_LIBRARY_DENYLIST, ...added])];
    }
  }
  target['use_tls'] = form.useTls;
  const iasp = form.iasp.trim();
  if (iasp) target['iasp'] = iasp;
  target['hex_field_ccsid'] = ccsid;
  const driver = form.driverName.trim();
  if (driver) target['driver_name'] = driver;
  target['include_source_files'] = form.includeSourceFiles;
  target['include_logical_files'] = form.includeLogicalFiles;
  return { ok: true, target };
}

export type EdgeScanReadiness = { ok: true; warning: string | null } | { ok: false; reason: string };

/**
 * Release 3.1 health words (lib/edge-health.ts): an agent that is not checking
 * in can still be given a job - it collects it when it comes back - but the
 * operator should know the scan will not start now.
 */
function notCheckingInWarning(status: EdgePodSummary['healthStatus']): string | null {
  if (status === 'silent') return 'This agent is Offline (no check-in within its window), so the scan starts only when it checks in again.';
  if (status === 'down') return 'This agent is Down (no check-in for a long time), so the scan starts only if it checks in again.';
  if (status === 'pending') return 'This agent has never checked in, so the scan starts only after its first check-in.';
  return null;
}

export function db2iScanReadiness(pod: EdgePodSummary | null, credential: string, scope: Db2ForIForm['scope'] = 'whole_system'): EdgeScanReadiness {
  if (!pod) return { ok: false, reason: 'Choose the edge agent that will run this scan.' };
  if (pod.healthStatus === 'decommissioned') return { ok: false, reason: 'This agent is decommissioned.' };
  const offline = notCheckingInWarning(pod.healthStatus);
  // `== null`, not `=== null`: an older controller omits the key entirely, so
  // the field arrives undefined, and that agent is just as unable to report.
  if (pod.capabilities == null) {
    if (scope === 'ifs') return { ok: false, reason: 'This agent is too old to report what it can scan. An IFS scan sent to it would scan the libraries instead, so upgrade it to release 3.6 or later first.' };
    const old = 'This agent is too old to report what it can scan or which credentials it holds, so the scan may fail. Upgrade the agent to check before queuing.';
    return { ok: true, warning: offline ? `${old} ${offline}` : old };
  }
  if (!pod.capabilities.scannerKinds.includes('db2_for_i')) {
    return { ok: false, reason: 'This agent was built without Db2 for i. Upgrade it to the standard scanner image.' };
  }
  // An agent without the IFS mode would ignore `ifs_path` and sweep every library.
  if (scope === 'ifs' && !pod.capabilities.scannerKinds.includes('db2_for_i_ifs')) {
    return { ok: false, reason: 'This agent does not support IFS scanning. Upgrade it to release 3.6 or later, or it would scan the libraries instead.' };
  }
  const name = credential.trim();
  if (name && !pod.capabilities.credentialNames.some((n) => sameCredentialName(n, name))) {
    return {
      ok: false,
      reason: `This agent has no credential named "${name}". Put the password in a file named ${name} in the agent's credentials directory (/run/secrets by default), set the environment variable ${credentialEnvVar(name)}, or bind it to Azure Key Vault, HashiCorp Vault or CyberArk with a file named ${name}.vault beside it, then wait for the next heartbeat.`,
    };
  }
  return { ok: true, warning: offline };
}

export function describeJobTarget(target: Record<string, unknown>): string {
  const kind = String(target['kind'] ?? '?');
  if (kind === 'db2_for_i') {
    if (target['ifs_path']) return `db2_for_i://${String(target['system'] ?? '')}/ifs${String(target['ifs_path'])}`;
    return `db2_for_i://${String(target['system'] ?? '')}/${String(target['library'] ?? '*ALL')}`;
  }
  return `${kind}://${String(target['host'] ?? '')}/${String(target['database'] ?? '')}.${String(target['schema'] ?? '')}`;
}
